import { Heading, LuIcon, Separator } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type ProjectAttachment, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfProject, selectProjectById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { AttachmentList } from "../../components/AttachmentList/AttachmentList"
import { AttachmentButton } from "../../components/Buttons/AttachmentButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/projects/$projectId/attachments")

export const ProjectSettingsAttachmentView = memo(() => {
	const { projectId } = routeApi.useParams()
	const project = useAppSelector(selectProjectById(projectId))

	if (!project) {
		throw new Error(`expected project to exist, got ${project}.`)
	}

	const attachments = useShallowEqaulSelector(selectAttachmentsOfProject(project.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.projectAttachments.bulkAdd(
				files.map((file) => ({
					project: project.uuid,
					file,
				})),
			)
		},
		[project.uuid],
	)

	const handleDeleteAttachment = useCallback(
		(fileAttachment: Stored<ProjectAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.projectAttachments.delete(fileAttachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Attachments</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<AttachmentButton size="sm" variant="surface" onAttachFiles={handleAttachFiles}>
							<LuIcon icon={modelIconMapping.add} />
							Add
						</AttachmentButton>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<AttachmentList
					attachments={attachments}
					onDelete={handleDeleteAttachment}
					service={sdk.projectAttachments}
				/>
			</section>
		</ViewContainer>
	)
})
ProjectSettingsAttachmentView.displayName = "ProjectSettingsAttachmentView"
